import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap, finalize, filter, take } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { AuthShopService } from '../../auth-shop/auth-shop.service';
import { AuthUtils } from '../auth.utils';
import { TiktokTokenStatus } from '../enums/tiktok-tokens-status.enum';
import { TiktokTokenCheckerService } from '../../tiktok-token-checker/tiktok-token-checker.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { LocalStorageKeys } from '../../constants/local-storage-keys.enum';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private refreshTokenInProgress$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private authShopService: AuthShopService,
    private tiktokTokenCheckerService: TiktokTokenCheckerService,
    private router: Router,
    private localStorage: LocalStorageService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip auth processing for the refresh endpoint — it uses the _rt cookie,
    // not the Authorization header. Without this exclusion, the interceptor
    // detects the expired access token and tries to refresh before sending the
    // refresh request itself, creating a deadlock.
    if (req.url.includes('/auth/refresh-access-token')) {
      return next.handle(req.clone({ withCredentials: true }));
    }

    const accessToken = this.authService.accessToken;
    const temporaryToken = this.authShopService.temporaryJwtToken;

    // Always add withCredentials for Elixir API requests
    if (req.url.includes('localhost:4001')) {
      const clonedReq = req.clone({ withCredentials: true });

      if (accessToken) {
        return this.handleJwtAuth(clonedReq, next, accessToken);
      } else if (temporaryToken) {
        return this.handleTemporaryAuth(clonedReq, next, temporaryToken);
      }

      return next.handle(clonedReq);
    }

    if (accessToken) {
      return this.handleJwtAuth(req, next, accessToken);
    } else if (temporaryToken) {
      return this.handleTemporaryAuth(req, next, temporaryToken);
    }

    return next.handle(req);
  }

  private handleJwtAuth(
    req: HttpRequest<any>,
    next: HttpHandler,
    accessToken: string
  ): Observable<HttpEvent<any>> {
    const tokenExpired = AuthUtils.isTokenExpired(accessToken);

    if (!tokenExpired) {
      const clonedRequest = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
        withCredentials: true
      });

      return next.handle(clonedRequest).pipe(
        tap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            // Primary: check response headers (guard sets these and lets data through)
            const tokenStatus = event.headers.get('x-tiktok-token-status');
            const headerShopId = event.headers.get('x-tiktok-shop-id');
            let tokenExpiredDetected = false;

            if (
              tokenStatus === TiktokTokenStatus.RefreshTokenExpired ||
              tokenStatus === TiktokTokenStatus.TokenDoesNotExist
            ) {
              this.tiktokTokenCheckerService.tikTokTokenSetShopId(headerShopId ?? '');
              this.tiktokTokenCheckerService.tikTokTokenSetState(false);
              tokenExpiredDetected = true;
            }

            // Fallback: check response body (legacy - body-based short-circuit is removed but kept for safety)
            if (
              !tokenExpiredDetected &&
              event.body &&
              (event.body?.status === TiktokTokenStatus.RefreshTokenExpired ||
                event.body?.status === TiktokTokenStatus.TokenDoesNotExist)
            ) {
              this.tiktokTokenCheckerService.tikTokTokenSetShopId(event.body.shopId);
              this.tiktokTokenCheckerService.tikTokTokenSetState(false);
              tokenExpiredDetected = true;
            }

            // Actively clear expired banner when token is valid.
            // Without this, a stale "expired" state persists indefinitely because
            // tikTokTokenSetState(true) was never called on successful responses.
            // distinctUntilChanged() in the service prevents redundant emissions.
            if (!tokenExpiredDetected) {
              this.tiktokTokenCheckerService.tikTokTokenSetState(true);
            }
          }
        }),
        catchError((error: HttpErrorResponse) => {
          if (this.isUserDeletedError(error)) {
            this.forceLogout();
            return throwError(() => ({
              status: error.status,
              message: 'Your account has been deleted from the system.',
              details: error.error || null,
              forceLogout: true
            }));
          }

          if (error.status === 401) {
            this.authService.signOut();
            this.router.navigate(['/login'], {
              queryParams: { returnUrl: req.url }
            });
          }
          return throwError(() => ({
            status: error.status,
            message: error.error?.message || error.message || 'An error occurred.',
            details: error.error || null
          }));
        })
      );
    }

    // Demo users: don't attempt token refresh — expired demo sessions can't be refreshed.
    // Without this guard, refresh succeeds (backend issues new JWT) but DemoMiddleware
    // still rejects the request (session expired), causing a retry loop.
    if (this.authService.isDemoMode()) {
      this.authService.signOut();
      this.router.navigate(['/login']);
      return throwError(() => new Error('Demo session expired'));
    }

    // Token expired — ALL requests go through refreshToken() which handles queuing
    return this.refreshToken(req, next);
  }

  private handleTemporaryAuth(
    req: HttpRequest<any>,
    next: HttpHandler,
    temporaryToken: string
  ): Observable<HttpEvent<any>> {
    const tokenExpired = AuthUtils.isTokenExpired(temporaryToken);

    if (tokenExpired) {
      this.localStorage.remove(LocalStorageKeys.TEMPORARY_JWT);
      this.router.navigate(['sign-up/create-account']);
      return throwError(() => new Error('Temporary token expired'));
    }

    const clonedRequest = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${temporaryToken}`)
    });

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.isUserDeletedError(error)) {
          this.forceLogout();
          return throwError(() => ({
            status: error.status,
            message: 'Your account has been deleted from the system.',
            details: error.error || null,
            forceLogout: true
          }));
        }

        if (error.status === 401) {
          this.localStorage.remove(LocalStorageKeys.TEMPORARY_JWT);
          this.router.navigate(['sign-up/create-account']);
        }
        return throwError(() => error);
      })
    );
  }

  private refreshToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.refreshTokenInProgress$.getValue()) {
      // Wait for ongoing refresh to complete, then retry with new token
      return this.refreshTokenInProgress$.pipe(
        filter(inProgress => !inProgress),
        take(1),
        switchMap(() => {
          const newAccessToken = this.authService.accessToken;
          if (newAccessToken && !AuthUtils.isTokenExpired(newAccessToken)) {
            const newRequest = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${newAccessToken}`),
              withCredentials: true
            });
            return next.handle(newRequest);
          }
          // Refresh failed — send without token (will get 401, handled by catchError)
          return next.handle(req);
        }),
        catchError(error => {
          if (this.isUserDeletedError(error)) {
            this.forceLogout();
            return throwError(() => ({
              status: error.status,
              message: 'Your account has been deleted from the system.',
              details: error.error || null,
              forceLogout: true
            }));
          }
          return throwError(() => error);
        })
      );
    }

    this.refreshTokenInProgress$.next(true);

    return this.authService.refreshAccessToken().pipe(
      switchMap((refreshSuccessful: boolean) => {
        if (refreshSuccessful) {
          const newAccessToken = this.authService.accessToken;
          const newRequest = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${newAccessToken}`),
            withCredentials: true
          });
          return next.handle(newRequest);
        }
        return next.handle(req);
      }),
      catchError(error => {
        if (this.isUserDeletedError(error)) {
          this.forceLogout();
          return throwError(() => ({
            status: error.status,
            message: 'Your account has been deleted from the system.',
            details: error.error || null,
            forceLogout: true
          }));
        }

        this.authService.signOut();
        this.router.navigate(['/login'], {
          queryParams: { returnUrl: req.url }
        });
        return throwError(() => error);
      }),
      finalize(() => {
        this.refreshTokenInProgress$.next(false);
      })
    );
  }

  private isUserDeletedError(error: HttpErrorResponse): boolean {
    if (error.status === 401 && error.error) {
      if (error.error.forceLogout === true) {
        return true;
      }

      const errorMessage = error.error.message || '';
      const errorType = error.error.error || '';

      return (
        errorType === 'User Deleted' ||
        errorMessage.includes('User has been deleted') ||
        errorMessage.includes('User deleted - force logout required') ||
        errorMessage.includes('User merchant meta not found')
      );
    }

    return false;
  }

  private forceLogout(): void {
    this.localStorage.remove(LocalStorageKeys.ACCESS_TOKEN);
    this.localStorage.remove(LocalStorageKeys.TEMPORARY_JWT);
    this.localStorage.remove(LocalStorageKeys.DEMO_MODE);
    this.localStorage.remove(LocalStorageKeys.DEMO_LOGGED_IN);
    this.localStorage.remove(LocalStorageKeys.DEMO_LAST_ACTIVITY);
    this.localStorage.remove(LocalStorageKeys.DEMO_TYPE);

    this.authService.signOut().subscribe();

    sessionStorage.setItem(
      'userDeletionMessage',
      'Oops! It looks like your account has been removed from our database and no longer exists. Please contact support at hello@dashboardly.io if you believe this is an error.'
    );

    this.router.navigate(['/login']);
  }
}
